import moment from "moment";

import { formatDate, isValidDate } from "../../../../utils/date-format.utils";
import { isDefined } from "../../../../utils/type.utils";
import { CAMPAIGN_TEMPLATE } from "../../../../types/templates/campaign-brief/campaign-brief-template.enum";

import { ICampaignBriefData } from "../campaign-brief-state/campaign-brief-state.interface";
import { UpdateCampaignBriefRequest } from "../update-campaign-brief-request.interface";

import { CampaignBriefForm } from "./campaign-brief-form.interface";

export function shouldResetBrief(
  currentState: ICampaignBriefData,
  changes: CampaignBriefForm
): boolean {
  return currentState.status === "in_progress" && changes.status === "draft";
}

export function shouldStartReview(
  updateResult: ICampaignBriefData,
  changes: CampaignBriefForm
): boolean {
  return changes.status === "readyForApproval" && updateResult.status === "draft";
}

export function mapCampaignBriefFormToParams(form: CampaignBriefForm): UpdateCampaignBriefRequest {
  return {
    additionalCreativeRequirementsDetails: form.additionalCreativeRequirementsDetails,
    additional_details: form.additionalDetails,
    announcementDate: formatDate(form.announcementDate),
    areVendorAssetsProvided: Boolean(form.areVendorAssetsProvided),
    audience: form.audience,
    background: form.background,
    brandFrameworkSupport: form.brandFrameworkSupport,
    content: form.campaignContent,
    context: form.campaignContext,
    customerMessage: form.customerMessage,
    customerTakeaway: form.customerTakeaway,
    endAt: formatDate(form.endAt),
    executionPlansDueAt: formatDate(form.executionPlansDueAt),
    hasVendorCommitment: form.hasVendorCommitment,
    insight: form.insight,
    isExclusive: form.isExclusive,
    isInstore: form.isInstore,
    isMap: form.isMap,
    isOnline: form.isOnline,
    isRegional: isDefined(form.isRegional) ? Boolean(form.isRegional) : null,
    keyFeatures: form.keyFeatures,
    languages: form.languages,
    launchDate: formatDate(form.launchDate),
    legalRequirementsDetails: form.legalRequirementsDetails,
    linkedCampaignBriefs: form.linkedCampaignBriefs
      ? getLinkedCampaignBriefIds(form.linkedCampaignBriefs)
      : [],
    mandatoryCreativeRequirementsDetails: form.mandatoryCreativeRequirementsDetails,
    marketingChannels: form.marketingChannels,
    marketingSupportLevel: form.marketingSupportLevel,
    messaging_primary: form.messagingPrimary,
    messaging_secondary: form.messagingSecondary,
    name: form.campaignName,
    numberOfStores: form.numberOfStores,
    objectives: form.objectives,
    offer_details: form.offerDetails,
    opportunity: form.opportunity,
    otherMarketingChannels: form.otherMarketingChannels,
    overview: form.campaignOverview,
    overviewAttachments: form.overviewAttachments,
    preOrderDate: formatDate(form.preOrderDate),
    previousResults: form.previousResults,
    productPlacement: form.productPlacement,
    regions: form.regions,
    resourcetype: form.campaignTemplate,
    startAt: formatDate(form.startAt),
    targetSalesForecast: form.targetSalesForecast === "" ? null : form.targetSalesForecast,
    targetTimeframe: form.targetTimeframe,
    targetUnitForecast: form.targetUnitForecast === "" ? null : form.targetUnitForecast,
    tonality: form.tonality,
    underNdaUsers: form.underNdaUsers?.map((user) => user.id),
    vendorAssetsLocation: form.vendorAssetsLocation,
    warehouseEta: formatDate(form.warehouseEta),
  };
}

export const createCampaignBriefDefaultValues = (brief: ICampaignBriefData): CampaignBriefForm => {
  switch (brief.resourcetype) {
    case CAMPAIGN_TEMPLATE.BRAND:
      return getBrandTemplateDefaultValues(brief);

    case CAMPAIGN_TEMPLATE.SIMPLE:
      return getSimpleLaunchTemplateDefaultValues(brief);

    case CAMPAIGN_TEMPLATE.NEW_PRODUCT:
      return getNewProductLaunchTemplateDefaultValues(brief);

    case CAMPAIGN_TEMPLATE.GENERAL:
    default:
      return getGeneralTemplateDefaultValues(brief);
  }
};

const getGeneralTemplateDefaultValues = (campaignBrief: ICampaignBriefData) => ({
  additionalCreativeRequirementsDetails: campaignBrief.additionalCreativeRequirementsDetails ?? "",
  areVendorAssetsProvided: campaignBrief.areVendorAssetsProvided ?? false,
  audience: campaignBrief.audience ?? "",
  campaignContext: campaignBrief.context ?? "",
  campaignOverview: campaignBrief.overview ?? "",
  endAt: isValidDate(campaignBrief.endAt ?? "") ? moment(campaignBrief.endAt) : null,
  executionPlansDueAt: isValidDate(campaignBrief.executionPlansDueAt ?? "")
    ? moment(campaignBrief.executionPlansDueAt)
    : null,
  isRegional: campaignBrief.isRegional ?? false,
  languages: campaignBrief.languages ?? [],
  legalRequirementsDetails: campaignBrief.legalRequirementsDetails ?? "",
  linkedCampaignBriefs: campaignBrief.linkedCampaignBriefsDetails ?? [],
  mandatoryCreativeRequirementsDetails: campaignBrief.mandatoryCreativeRequirementsDetails ?? "",
  marketingChannels: campaignBrief.marketingChannels ?? [],
  messagingPrimary: campaignBrief.messagingPrimary ?? "",
  messagingSecondary: campaignBrief.messagingSecondary ?? "",
  objectives: campaignBrief.objectives ?? "",
  offerDetails: campaignBrief.offerDetails ?? "",
  otherMarketingChannels: campaignBrief.otherMarketingChannels ?? "",
  overviewAttachments: campaignBrief.overviewAttachments ?? [],
  previousResults: campaignBrief.previousResults ?? "",
  regions: campaignBrief.regions ?? [],
  startAt: isValidDate(campaignBrief.startAt ?? "") ? moment(campaignBrief.startAt) : null,
  status: campaignBrief.status ?? "draft",
  underNdaUsers: campaignBrief.underNdaUserDetails ?? [],
  vendorAssetsLocation: campaignBrief.vendorAssetsLocation ?? "",
});

export const getBrandTemplateDefaultValues = (
  campaignBrief: ICampaignBriefData
): CampaignBriefForm => ({
  additionalDetails: campaignBrief.additionalDetails ?? "",
  areVendorAssetsProvided: campaignBrief.areVendorAssetsProvided ?? false,
  brandFrameworkSupport: campaignBrief.brandFrameworkSupport ?? "",
  campaignContent: campaignBrief.content ?? "",
  campaignContext: campaignBrief.context ?? "",
  customerTakeaway: campaignBrief.customerTakeaway ?? "",
  customerMessage: campaignBrief.customerMessage ?? "",
  endAt: isValidDate(campaignBrief.endAt ?? "") ? moment(campaignBrief.endAt) : null,
  executionPlansDueAt: isValidDate(campaignBrief.executionPlansDueAt ?? "")
    ? moment(campaignBrief.executionPlansDueAt)
    : null,
  insight: campaignBrief.insight ?? "",
  isRegional: campaignBrief.isRegional ?? false,
  languages: campaignBrief.languages ?? [],
  legalRequirementsDetails: campaignBrief.legalRequirementsDetails ?? "",
  linkedCampaignBriefs: campaignBrief.linkedCampaignBriefsDetails ?? [],
  marketingChannels: campaignBrief.marketingChannels ?? [],
  opportunity: campaignBrief.opportunity ?? "",
  otherMarketingChannels: campaignBrief.otherMarketingChannels ?? "",
  overviewAttachments: campaignBrief.overviewAttachments ?? [],
  regions: campaignBrief.regions ?? [],
  startAt: isValidDate(campaignBrief.startAt ?? "") ? moment(campaignBrief.startAt) : null,
  status: campaignBrief.status ?? "draft",
  tonality: campaignBrief.tonality ?? "",
  underNdaUsers: campaignBrief.underNdaUserDetails ?? [],
  vendorAssetsLocation: campaignBrief.vendorAssetsLocation ?? "",
});

export const getSimpleLaunchTemplateDefaultValues = (
  campaignBrief: ICampaignBriefData
): CampaignBriefForm => ({
  additionalDetails: campaignBrief.additionalDetails ?? "",
  areVendorAssetsProvided: campaignBrief.areVendorAssetsProvided ?? false,
  campaignContext: campaignBrief.context ?? "",
  campaignOverview: campaignBrief.overview ?? "",
  endAt: isValidDate(campaignBrief.endAt ?? "") ? moment(campaignBrief.endAt) : null,
  executionPlansDueAt: isValidDate(campaignBrief.executionPlansDueAt ?? "")
    ? moment(campaignBrief.executionPlansDueAt)
    : null,
  isRegional: campaignBrief.isRegional ?? false,
  languages: campaignBrief.languages ?? [],
  legalRequirementsDetails: campaignBrief.legalRequirementsDetails ?? "",
  linkedCampaignBriefs: campaignBrief.linkedCampaignBriefsDetails ?? [],
  marketingChannels: campaignBrief.marketingChannels ?? [],
  otherMarketingChannels: campaignBrief.otherMarketingChannels ?? "",
  overviewAttachments: campaignBrief.overviewAttachments ?? [],
  regions: campaignBrief.regions ?? [],
  startAt: isValidDate(campaignBrief.startAt ?? "") ? moment(campaignBrief.startAt) : null,
  status: campaignBrief.status ?? "draft",
  underNdaUsers: campaignBrief.underNdaUserDetails ?? [],
  vendorAssetsLocation: campaignBrief.vendorAssetsLocation ?? "",
});

export const getNewProductLaunchTemplateDefaultValues = (
  campaignBrief: ICampaignBriefData
): CampaignBriefForm => ({
  additionalDetails: campaignBrief.additionalDetails ?? "",
  announcementDate: isValidDate(campaignBrief.announcementDate ?? "")
    ? moment(campaignBrief.announcementDate)
    : null,
  areVendorAssetsProvided: campaignBrief.areVendorAssetsProvided ?? false,
  background: campaignBrief.background ?? "",
  campaignContext: campaignBrief.context ?? "",
  endAt: isValidDate(campaignBrief.endAt ?? "") ? moment(campaignBrief.endAt) : null,
  executionPlansDueAt: isValidDate(campaignBrief.executionPlansDueAt ?? "")
    ? moment(campaignBrief.executionPlansDueAt)
    : null,
  hasVendorCommitment: campaignBrief.hasVendorCommitment ?? false,
  isExclusive: campaignBrief.isExclusive ?? false,
  isInstore: campaignBrief.isInstore ?? false,
  isMap: campaignBrief.isMap ?? false,
  isOnline: campaignBrief.isOnline ?? false,
  isRegional: campaignBrief.isRegional ?? false,
  keyFeatures: campaignBrief.keyFeatures ?? "",
  languages: campaignBrief.languages ?? [],
  launchDate: isValidDate(campaignBrief.launchDate ?? "") ? moment(campaignBrief.launchDate) : null,
  legalRequirementsDetails: campaignBrief.legalRequirementsDetails ?? "",
  linkedCampaignBriefs: campaignBrief.linkedCampaignBriefsDetails ?? [],
  marketingChannels: campaignBrief.marketingChannels ?? [],
  marketingSupportLevel: campaignBrief.marketingSupportLevel ?? "",
  numberOfStores: campaignBrief.numberOfStores ?? "",
  otherMarketingChannels: campaignBrief.otherMarketingChannels ?? "",
  overviewAttachments: campaignBrief.overviewAttachments ?? [],
  preOrderDate: isValidDate(campaignBrief.preOrderDate ?? "")
    ? moment(campaignBrief.preOrderDate)
    : null,
  productPlacement: campaignBrief.productPlacement ?? "",
  regions: campaignBrief.regions ?? [],
  startAt: isValidDate(campaignBrief.startAt ?? "") ? moment(campaignBrief.startAt) : null,
  status: campaignBrief.status ?? "draft",
  targetSalesForecast: campaignBrief.targetSalesForecast ?? "",
  targetTimeframe: campaignBrief.targetTimeframe ?? "",
  targetUnitForecast: campaignBrief.targetUnitForecast ?? "",
  underNdaUsers: campaignBrief.underNdaUserDetails ?? [],
  vendorAssetsLocation: campaignBrief.vendorAssetsLocation ?? "",
  warehouseEta: isValidDate(campaignBrief.warehouseEta ?? "")
    ? moment(campaignBrief.warehouseEta)
    : null,
});

export const createCampaignBriefForm = (
  brief: ICampaignBriefData,
  name: string,
  data?: any
): CampaignBriefForm => ({
  id: brief.id,
  campaignName: name,
  campaignTemplate: brief.resourcetype,
  linkedCampaignBriefs: data.linkedCampaignBriefs || brief.linkedCampaignBriefsDetails,
  ...data,
});

export const getOnNameChangeRequiredProperties = (
  brief: ICampaignBriefData
): Partial<ICampaignBriefData> => ({
  startAt: brief.startAt,
  endAt: brief.endAt,
  executionPlansDueAt: brief.executionPlansDueAt,
  areVendorAssetsProvided: brief.areVendorAssetsProvided,
  isRegional: brief.isRegional,
  launchDate: brief.launchDate,
  linkedCampaignBriefsDetails: brief.linkedCampaignBriefsDetails,
});

function getLinkedCampaignBriefIds(linkedBriefs: any[]): number[] {
  return linkedBriefs.map((brief) => brief.id) || [];
}
